import { ButtonType, CheckBoxWithLabelWidget, DialogFooterButtonsWidget, DialogPositioningBehavior, DialogType } from '@tableau/widgets';
import { IPromise } from 'angular';
import React from 'react';
import _ from 'underscore';
import { Messages } from '../../codegen/Localize';
import { ISettingsFormScope, SettingsFormService } from '../../settings/directives/tbSettingsCoordinator';
import { ServerSettingsInstance } from '../../settings/services/ServerSettings';
import { ISiteSettingsForServerAdmin } from '../../settings/types/ISite';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { L10n } from '../../ts/L10n/L10n';
import { GetSitesRequest, IGetSitesResult } from '../../ts/ServerApi/requests/GetSitesRequest';
import { IEditableServerSettings } from '../../ts/ServerApi/types/IEditableServerSettings';
import { WebHttpClientWithErrorToastInstance } from '../../httpClient/WebHttpClientWithErrorToast';
import { ReactModalService } from '../../commonInfra/dialogs/ReactModalService';
import { getAppStore } from '../AppStore';
import { SessionUserSelectors } from '../selectors/SessionInfoSelectors';
import { ServiceLocator } from '../ServiceLocator';
import { runNowEnabledSiteDialogProps } from './RunNowEnabledSiteDialog';
import { SiteSettingsStyles } from './styles/SiteSettingsStyles';
import { IPage } from '~src/ts/ServerApi/types/IPage';


interface ISiteRunNowEnabledMap {
    [site: string]: boolean;
}

interface ISiteIDMap {
    [site: string]: string;
}

interface RunNowEnabledServerSettingProps {
    settings: IEditableServerSettings;
}

interface RunNowEnabledServerSettingState {
    isGloballyEnabled: boolean;
    isEnabledForSite?: ISiteRunNowEnabledMap;
    intialState?: {
        isGloballyEnabled: boolean;
        isEnabledForSite: ISiteRunNowEnabledMap;
    };
    siteIDMap?: ISiteIDMap;
}

export class RunNowEnabledServerSetting extends React.PureComponent<RunNowEnabledServerSettingProps, RunNowEnabledServerSettingState> {
    private shouldRender: boolean;

    constructor(props: RunNowEnabledServerSettingProps) {
        super(props);

        this.shouldRender = FeatureFlags.enableRunNowEnabled && SessionUserSelectors.isSessionMultiSite(getAppStore().getState());
        if (!this.shouldRender) {
            return;
        }

        // The rest of the state is populated asynchronously
        this.state = {
            isGloballyEnabled: this.props.settings.runNowEnabled,
        };
        this.openGlobalToggleDialog = this.openGlobalToggleDialog.bind(this);
        this.openSiteSelectionDialog = this.openSiteSelectionDialog.bind(this);
        this.cancel = this.cancel.bind(this);
        this.save = this.save.bind(this);
        this.runNowGlobalSettingChanged = this.runNowGlobalSettingChanged.bind(this);
        this.runNowSiteSettingsChanged = this.runNowSiteSettingsChanged.bind(this);

        const formService = ServiceLocator.get<SettingsFormService>('SettingsFormService');
        const formState: ISettingsFormScope = {
            formController: {
                $pristine: true,
                $dirty: false,
                $valid: true,
                $invalid: true,
                $submitted: false,
                $error: {},
                $addControl: () => { },
                $removeControl: () => { },
                $setValidity: () => { },
                $setDirty: () => { },
                $setPristine: () => { },
                $setSubmitted: () => { },
                $setUntouched: () => { },
                $commitViewValue: () => { },
                $rollbackViewValue: () => { },
            },
            cancel: this.cancel,
            save: this.save,

            // "Optional" but actually required
            isBusy: () => false,
            isFormUnchanged: () => {
                return !this.runNowGlobalSettingChanged() && this.runNowSiteSettingsChanged().length === 0;
            },
            isValid: () => true,
        };
        formService.registerFormStateByName(formState, 'Run Now Settings');

        // Actually get the site specific settings
        const sitesPromise = RunNowEnabledServerSetting.getSiteEnabledMap();
        sitesPromise.then(sites => {
          let siteEnabledMap: ISiteRunNowEnabledMap = {};
          let siteIDMap: ISiteIDMap = {};
          for (let site of sites) {
              siteEnabledMap[site.settings.name] = site.settings.runNowEnabled;
              siteIDMap[site.settings.name] = site.id;
          }
          this.setState({
              isEnabledForSite: siteEnabledMap,
              intialState: {
                  isEnabledForSite: siteEnabledMap,
                  isGloballyEnabled: this.props.settings.runNowEnabled || false
              },
              siteIDMap,
          });
        });
    }

    public static async getSiteEnabledMap(): Promise<ISiteSettingsForServerAdmin[] > {
        let moreItems = true;
        let sites: ISiteSettingsForServerAdmin[] = [];
        while(moreItems) {
          const page: IPage = {
            startIndex: sites.length,
            maxItems: 100,
          };
          const request = new GetSitesRequest({page});
          const response: IGetSitesResult = await WebHttpClientWithErrorToastInstance.sendRequest(request);
          sites = sites.concat(response.sites);
          moreItems = response.totalCount > sites.length;
        }
        return sites;
    }

    private openGlobalToggleDialog(): void {
        const siteEnabledStatuses = _.values(this.state.isEnabledForSite);
        const numberOfSitesEnabled = siteEnabledStatuses.filter(enabled => enabled).length;
        const confirmGlobalToggleTitle = this.state.isGloballyEnabled
            ? Messages.runNowDisable()
            : Messages.runNowEnable();
        const confirmGlobalToggleText = this.state.isGloballyEnabled
            ? Messages.runNowDisableConfirmationText({ NUMBER_OF_SITES_ENABLED_LOC: L10n.localizedCount(numberOfSitesEnabled) })
            : Messages.runNowEnableConfirmationText({ NUMBER_OF_SITES_ENABLED_LOC: L10n.localizedCount(numberOfSitesEnabled) });
        const modal = ReactModalService.open({
            title: confirmGlobalToggleTitle,
            testId: 'globally-toggle-runnow-dialog',
            type: DialogType.NormalWithTitleBar,
            positioningBehavior: DialogPositioningBehavior.Centered,
            dialogContent: <>{ confirmGlobalToggleText }</>,
            footerContent: <DialogFooterButtonsWidget
                buttons={[{
                    handleClick: () => {
                        modal.close();
                    },
                    buttonType: ButtonType.Grey,
                    children: Messages.runNowDialogCancel(),
                    testId: 'footer-cancel'
                }, {
                    handleClick: () => {
                        this.setState({
                            isGloballyEnabled: !this.state.isGloballyEnabled
                        }, () => {
                            // We then have to tell angular to actually check that the form changed
                            const context = ServiceLocator.getAngularActionContext();
                            context.scope.$apply();
                        });
                        modal.close();
                    },
                    buttonType: ButtonType.Go,
                    children: Messages.runNowDialogYes(),
                    testId: 'footer-yes'
                }]}
                testId="footer"
            />
        });
    }

    private openSiteSelectionDialog(): void {
        if (!this.state.isGloballyEnabled) {
            return;
        }
        const modal = ReactModalService.open(runNowEnabledSiteDialogProps({
            runNowEnabledStates: this.state.isEnabledForSite,
            updateSites: (newState: { [site: string]: boolean }) => {
                this.setState({
                    isEnabledForSite: {
                        ...newState
                    }
                }, () => {
                    // We then have to tell angular to actually check that the form changed
                    const context = ServiceLocator.getAngularActionContext();
                    context.scope.$apply();
                });
            },
            updateAll: (state) => this.setState({
                isGloballyEnabled: state,
            }),
            close: () => { modal.close(); },
        }));
    }

    render() {
        if (!this.shouldRender || !this.state.isEnabledForSite) {
            return null;
        }
        const siteEnabledStatuses = _.values(this.state.isEnabledForSite);
        const numberOfSitesEnabled = siteEnabledStatuses.filter(enabled => enabled).length;
        const allSitesEnabled = numberOfSitesEnabled === siteEnabledStatuses.length;
        const editSitesDescription = this.state.isGloballyEnabled
            ? allSitesEnabled
                ? Messages.runNowGlobalToggleDialogAllSitesAllowed({ SITES_COUNT_LOC: L10n.localizedCount(numberOfSitesEnabled) })
                : Messages.runNowGlobalToggleDialogSitesAllowed({ SITES_COUNT_LOC: L10n.localizedCount(numberOfSitesEnabled) })
            : Messages.runNowGlobalToggleDialogSitesAllowed({ SITES_COUNT_LOC: L10n.localizedCount(0) });
        /* eslint-disable jsx-a11y/no-static-element-interactions */
        /* eslint-disable jsx-a11y/click-events-have-key-events */
        /* eslint-disable jsx-a11y/anchor-is-valid */
        return <>
            <div style={SiteSettingsStyles.HeaderStyle}>{Messages.runNowEnabledSettingHeader()}</div>
            <div style={SiteSettingsStyles.BodyStyle}>
                <p>{Messages.runNowEnabledSettingDescription()}</p>
                <CheckBoxWithLabelWidget
                    containerStyle={SiteSettingsStyles.CheckboxStyle}
                    checked={this.state.isGloballyEnabled}
                    handleChange={this.openGlobalToggleDialog}
                    testId="runnow-globally-enable"
                    label={Messages.runNowGlobalToggleDialogCheckboxLabel()}
                />
                <p>{editSitesDescription}<b>
                    <a style={!this.state.isGloballyEnabled ? SiteSettingsStyles.DisabledLink : {}} onClick={this.openSiteSelectionDialog}>
                        {Messages.runNowGlobalToggleDialogLinkText()}
                    </a>
                </b></p>
            </div>
        </>;
    }

    // Form method
    private cancel(): void {
        this.setState({
            isGloballyEnabled: this.props.settings.runNowEnabled || false,
            isEnabledForSite: this.state.intialState && this.state.intialState.isEnabledForSite,
        });
    }

    private runNowGlobalSettingChanged(): boolean {
        return this.state.intialState && this.state.intialState.isGloballyEnabled !== this.state.isGloballyEnabled;
    }

    private runNowSiteSettingsChanged(): string[] {
        if (!this.state.intialState || !this.state.isEnabledForSite) {
            return [];
        }
        return _.filter(
            _.keys(this.state.isEnabledForSite),
            (site: string) => this.state.intialState.isEnabledForSite[site] !== this.state.isEnabledForSite[site]
        );
    }

    // Form method
    private save(): IPromise<any> {
        const serverSettings = ServerSettingsInstance;
        const requests = [];

        // Update the global toggle, if necessary
        if (this.runNowGlobalSettingChanged()) {
            // This enables us to get a compile time failure if someone changes the key and doesn't update this line
            const runNowEnbabled: keyof IEditableServerSettings = 'runNowEnabled';
            requests.push(serverSettings.updateBoolean(runNowEnbabled, this.state.isGloballyEnabled));
        }
        // Update the per site settings, if necessary
        const modifiedSites = this.runNowSiteSettingsChanged();
        if (modifiedSites.length > 0) {
            // we need to make two requests, one for all of the ones we're setting to true, one for all the ones we're setting to false
            let sitesToEnable: string[] = [];
            let sitesToDisable: string[] = [];
            modifiedSites.forEach(siteName => {
                if (this.state.isEnabledForSite[siteName]) {
                    sitesToEnable.push(this.state.siteIDMap[siteName]);
                } else {
                    sitesToDisable.push(this.state.siteIDMap[siteName]);
                }
            });
            requests.push(serverSettings.updateRunNowSettings(sitesToEnable, true));
            requests.push(serverSettings.updateRunNowSettings(sitesToDisable, false));
        }
        // Always true at this point but typescript doesn't know that
        if (this.state.intialState && this.state.isEnabledForSite) {
            this.setState({
                intialState: {
                    isEnabledForSite: this.state.isEnabledForSite,
                    isGloballyEnabled: this.state.isGloballyEnabled,
                }
            });
        }
        const $q = ServiceLocator.get<ng.IQService>('$q');
        return $q.all(requests);
    }
}
